/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  Filter,
  ListContextProvider,
  ListToolbar,
  NumberField,
  SelectInput,
  TextField,
  useList,
  useListContext,
} from "react-admin";

import {
  AddAllIcon,
  CancelIcon,
  CloseIcon,
  CreateIcon,
} from "../../../components/common/iconography";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import { CustomField } from "../../../components/common/CustomFields";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { Empty } from "../../../components/common/Empty";
import {
  isBulkCommodity,
  isShippingContainer,
} from "../../../utils/loadTypeUtils";
import { isSubscriptionDisabled } from "../../../utils/appUtils";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
}));

const ListFilter = ({
  allProducts,
  setProducts,
  yardCommodity,
  setSelectedYardCommodity,
  ...props
}) => {
  const yardCommodities = window.appUser.reloadYard.activeYardCommodities.items;

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const newProducts = allProducts.filter(
      (itm) =>
        itm.name?.toLowerCase().includes(searchTerm) ||
        itm.productCode?.toLowerCase().includes(searchTerm) ||
        itm.category?.toLowerCase().includes(searchTerm) ||
        itm.customFields?.join().toLowerCase().includes(searchTerm),
    );
    setProducts(newProducts);
  };

  const handleLoadTypeFilter = (value) => {
    setSelectedYardCommodity(yardCommodities.find((itm) => itm.id === value));
  };

  return (
    <Filter {...props} variant="outlined">
      {!isBulkCommodity(yardCommodity) &&
        !isShippingContainer(yardCommodity) && (
          <SelectInput
            source="loadTypeFilter"
            label="Load Type"
            margin="dense"
            variant="outlined"
            alwaysOn
            choices={yardCommodities}
            optionText={(choice) =>
              `${choice.commodity.name} - ${choice.units}`
            }
            optionValue="id"
            allowEmpty={false}
            onChange={(evt) => handleLoadTypeFilter(evt.target.value)}
          />
        )}
      <SearchField
        label="Catalog Search"
        margin="dense"
        variant="outlined"
        alwaysOn
        handleSearch={handleSearch}
        searchOnChange={true}
      />
    </Filter>
  );
};

const PaginationToolbar = (props) => {
  const listContext = useListContext(props);

  return (
    <Grid container direction="row" spacing={1} alignItems="center">
      <Grid item xs={4}>
        <Typography>
          {listContext?.selectedIds?.length > 0 ? (
            `${listContext.selectedIds.length} selected`
          ) : (
            <>&nbsp;</>
          )}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Pagination {...props} {...listContext} />
      </Grid>
    </Grid>
  );
};

export const CatalogSearchDialog = ({
  backPath,
  form,
  account,
  allProducts,
  yardCommodity,
  isOpen,
  setIsOpen,
  handleAddSelected,
  ...props
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [loaded, setLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedYardCommodity, setSelectedYardCommodity] =
    useState(yardCommodity);

  const fixedFilter = {
    active: "true",
    ...(selectedYardCommodity?.id
      ? { reloadYardCommodityId: selectedYardCommodity.id }
      : {}),
  };
  const defaultSort = { field: "name", order: "ASC" };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = sessionStorage.getItem(`CatalogSearchDialog_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  useEffect(() => {
    if (allProducts) {
      setProducts(allProducts);
      setLoaded(true);
    }
  }, [allProducts]);

  const listContext = useList({
    ...props,
    data: products,
    ids: products.map(({ id }) => id),
    loaded: loaded,
    filter: fixedFilter,
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/products",
    resource: "products",
  });

  useEffect(() => {
    if (isBulkCommodity(yardCommodity?.commodity)) {
      // Bulk can only selecte one product - cannot mix products
      if (listContext.selectedIds?.length === 2)
        listContext.onSelect([listContext.selectedIds[1]]);
    }
  }, [listContext.selectedIds]);

  useEffect(() => {
    if (listContext?.perPage) {
      sessionStorage.setItem(
        `CatalogSearchDialog_perPage`,
        listContext.perPage,
      );
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  const handleAdd = () => {
    debugger;
    handleAddSelected(listContext);
    handleClose();
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") {
      return; // ignore backdrop clicks
    }
    setIsOpen(false);
    listContext.onUnselectItems();
    setProducts(allProducts);
  };

  const handleCreateProduct = () => {
    const formData = form.getState().values;
    history.push({
      pathname: "/products/create",
      state: {
        account: account,
        savedRecord: formData,
        backPath: backPath
          ? [...backPath, history.location.pathname]
          : [history.location.pathname],
        _scrollToTop: true,
      },
    });
  };

  return (
    <Dialog
      maxWidth="xl"
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={false}
      fullWidth
    >
      <ListContextProvider value={listContext}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <ListToolbar
            {...props}
            filters={
              <ListFilter
                allProducts={allProducts}
                setProducts={setProducts}
                yardCommodity={yardCommodity}
                setSelectedYardCommodity={setSelectedYardCommodity}
              />
            }
            filterValues={{ loadTypeFilter: selectedYardCommodity.id }}
          />
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className={classes.dialogContent}>
          <CatalogGrid
            {...props}
            {...listContext}
            basePath={"/products"}
            resource={"products"}
            backPath={backPath}
            form={form}
            account={account}
            selectedYardCommodity={selectedYardCommodity}
          />
          <PaginationToolbar {...props} />
        </DialogContent>
        <DialogActions>
          <Button
            label="Create"
            color="primary"
            style={{ marginRight: "auto" }}
            onClick={handleCreateProduct}
            disabled={isSubscriptionDisabled()}
          >
            <CreateIcon />
          </Button>
          <Button
            label="Cancel"
            color="defalut"
            autoFocus
            onClick={handleClose}
          >
            <CancelIcon />
          </Button>
          <Button
            label="Add Selected"
            color="primary"
            onClick={handleAdd}
            disabled={listContext?.selectedIds?.length === 0}
          >
            <AddAllIcon />
          </Button>
        </DialogActions>
      </ListContextProvider>
    </Dialog>
  );
};

const CatalogGrid = ({
  backPath,
  form,
  account,
  selectedYardCommodity,
  ...props
}) => {
  const { ids, page } = props;
  const hasBulkActions = true;
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const formData = form.getState().values;
  const accountFields =
    account?.inventoryCustomFields?.length > 0
      ? account.inventoryCustomFields
      : [];
  const customGridFields = accountFields
    ? accountFields.map((field, index) => {
        return (
          <CustomField
            source={`customFields[${index}]`}
            field={field}
            label={field.name}
            sortable={true}
          />
        );
      })
    : [];

  if (ids.length === 0 && page === 1)
    return (
      <Empty
        {...props}
        backPath={backPath}
        state={{
          account: account,
          savedRecord: formData,
        }}
        message="Create a new Product"
      />
    );
  return isSmall ? (
    <CustomizableDatagrid
      label={`Products by: ${selectedYardCommodity?.commodity.name}`}
      rowClick={false}
      hasBulkActions={hasBulkActions}
      storageProp={`CatalogSearchDialog_${account?.id}_small`}
      defaultColumns={["productCode", "name", "defaultUOM"]}
    >
      {[
        <TextField source="productCode" label="Product Code" />,
        <TextField source="name" label="Name" />,
        <TextField source="defaultUOM" label="UOM" />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  ) : (
    <CustomizableDatagrid
      label={`Products by: ${selectedYardCommodity?.commodity.name}`}
      rowClick={false}
      hasBulkActions={hasBulkActions}
      storageProp={`CatalogSearchDialog_${account?.id}_large`}
      defaultColumns={[
        "productCode",
        "name",
        "category",
        "totalQuantityOnHand",
        "totalQuantityCommitted",
        "totalQuantityAvailable",
        "defaultUOM",
      ]}
    >
      {[
        <TextField source="productCode" label="Product Code" />,
        <TextField source="name" label="Name" />,
        <TextField source="category" label="Commodity Category" />,
        <NumberField source="totalQuantityOnHand" label="On Hand" />,
        <NumberField source="totalQuantityCommitted" label="Committed" />,
        <NumberField source="totalQuantityAvailable" label="Available" />,
        <TextField source="defaultUOM" label="UOM" />,
        <NumberField source="pcs" label="Pieces" />,
        ...customGridFields,
      ]}
    </CustomizableDatagrid>
  );
};
